<script setup lang="ts">
// import EmojiPicker from 'vue3-emoji-picker'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useChat } from 'ai/vue'
import { useFileDialog, useMouseInElement, usePermission } from '@vueuse/core'
import CloseButton from '../CloseButton.vue'

// import {
//   Popover,
//   PopoverContent,
//   PopoverTrigger,
// } from '@/components/ui/popover'
import { useChatStore } from '@/stores/useChat'
import { useStreamingChat } from '@/composables/useStreamingChat'

// import { cn } from '@/lib/utils'
import { getLocalStream, toDataURL } from '@/helpers'
import useSpeechToText from '@/composables/useSpeechToText'
import getNetwatchUser from '@/lib/getNetwatchUser'

const props = defineProps<{
  api?: string
  clientId?: string
  pendingQuestion?: string
}>()

const emit = defineEmits<{
  questionSent: []
}>()

const extraBottomSpace = defineModel()

// Use streaming if enabled, otherwise fall back to regular chat
const streamingEnabled = ref(true) // Feature flag for streaming

const { append, messages } = useChat({
  api: props.api,
  headers: {
    'x-client-id': props.clientId ?? 'random-client-id',
    'x-username': getNetwatchUser(),
  },
})

// Import streaming functionality
const { sendStreamingMessage, isLoading: streamingIsLoading } = useStreamingChat({
  api: props.api,
  clientId: props.clientId,
  username: getNetwatchUser(),
})

const chatStore = useChatStore()

const { isListening, startListening, stopListening, transcribedText, speechEnabled, timer } = useSpeechToText()
const microphoneAccess = usePermission('microphone')

const canReply = ref(false)
const messageReceived = ref(true)

// const emojiMenuOpen = ref(false)
const preEl = ref<HTMLPreElement>()
const newLines = ref(0)
const textareaEl = ref<HTMLTextAreaElement>()
const textareaValue = ref<string>('')
const extraInputHeight = ref(0)
const imageUrl = ref<string | undefined>()
const imageBase64 = ref<string | ArrayBuffer | null>()
const imageEl = ref()
const hovered = ref(true)

const { isOutside } = useMouseInElement(imageEl)

watch(isOutside, (o) => {
  hovered.value = !o
})

// Watch for pending questions and auto-send them
// Use immediate: true so it fires when component mounts with existing value (e.g., after tab switch)
watch(() => props.pendingQuestion, (newQuestion) => {
  if (newQuestion && newQuestion.trim() !== '') {
    textareaValue.value = newQuestion
    // Use nextTick to ensure DOM is ready before accessing refs
    setTimeout(() => {
      if (preEl.value) {
        countNewLines()
      }
      sendMessage()
      emit('questionSent')
    }, 150)
  }
}, { immediate: true })

const { files, open, reset } = useFileDialog({
  accept: 'image/*',
  directory: false,
  multiple: false,
})

function clearImage() {
  reset()
  imageUrl.value = undefined
  imageBase64.value = null
}

watch(files, (f) => {
  imageUrl.value = f ? URL.createObjectURL(f[0]) : undefined
  if (imageUrl.value) {
    toDataURL(imageUrl.value, (dataUrl) => {
      imageBase64.value = dataUrl
    })
  }
})

function startRecording() {
  if (microphoneAccess.value === 'granted') {
    startListening()
  }
  else {
    // eslint-disable-next-line no-alert
    alert('Please enable your microphone.')
    getLocalStream()
  }
}

// function toggleEmojiMenu() {
//   emojiMenuOpen.value = !emojiMenuOpen.value
// }

function countNewLines() {
  if (textareaValue.value && textareaValue.value.trim().length > 0)
    canReply.value = true
  else
    canReply.value = false
  
  // Guard against preEl not being available yet
  if (!preEl.value) return
  
  newLines.value = Math.floor((preEl.value.offsetHeight - 55) / 19)
  extraInputHeight.value = Math.min(19 * (newLines.value < 0 ? 0 : newLines.value), 200)
  extraBottomSpace.value = Math.min(19 * (newLines.value < 0 ? 0 : newLines.value), 200 - 56)
}

// function onSelectEmoji(emoji: any) {
//   textareaValue.value = textareaValue.value + emoji.i
//   countNewLines()
//   toggleEmojiMenu()
//   setTimeout(() => {
//     textareaEl.value?.focus()
//   }, 500)
// }

function sendAudio() {
  stopListening()
  textareaValue.value = transcribedText.value
  setTimeout(() => {
    countNewLines()
  }, 100)
}

function deleteAudio() {
  stopListening()
}

async function sendMessage() {
  const message = textareaValue.value
  if (message.trim() !== '') {
    if (imageBase64.value !== null && imageBase64.value !== undefined) {
      const imageMessage = { image: (imageBase64.value as string), text: message }
      chatStore.updateResponses({
        isReply: true,
        messageContent: { image: imageBase64.value, text: message },
        isBeforeMessage: true,
        isAfterMessage: true,
        time: new Date(),
      })

      extraBottomSpace.value = 0
      extraInputHeight.value = 0
      messageReceived.value = false
      canReply.value = false
      textareaValue.value = ''
      clearImage()

      setTimeout(() => {
        chatStore.updateResponses({
          isBeforeMessage: true,
          isAfterMessage: true,
          isLoadingMessage: true,
          time: new Date(),
        })
      }, 200)

      append({
        // @ts-expect-error types
        content: imageMessage,
        role: 'user',
      }).then(() => {
        chatStore.deleteLastResponse(messages.value[messages.value.length - 1].content)

        window.postMessage({
          isForChat: true,
          messageSent: true,
        })

        messageReceived.value = true
      })
    }
    else {
      // Add user message to store
      chatStore.updateResponses({
        isReply: true,
        messageContent: message,
        isBeforeMessage: true,
        isAfterMessage: true,
        time: new Date(),
      })

      window.postMessage({
        isForChat: true,
        messageSent: true,
      })
      
      extraBottomSpace.value = 0
      extraInputHeight.value = 0
      messageReceived.value = false
      canReply.value = false
      textareaValue.value = ''

      try {
        if (streamingEnabled.value) {
          // Use streaming chat
          await sendStreamingMessage([
            {
              role: 'user',
              content: message,
            },
          ])
        }
        else {
          // Fall back to regular chat
          setTimeout(() => {
            chatStore.updateResponses({
              isBeforeMessage: true,
              isAfterMessage: true,
              isLoadingMessage: true,
              time: new Date(),
            })
          }, 200)

          if (messages.value[messages.value.length - 2] && typeof messages.value[messages.value.length - 2].content !== 'string') {
            messages.value = messages.value.map((v) => {
              if (typeof v.content === 'string') {
                return v
              }
              else {
                return {
                  ...v,
                  // @ts-expect-error types
                  content: v.content.text,
                }
              }
            })
          }

          await append({
            content: message,
            role: 'user',
          })
          
          chatStore.deleteLastResponse(messages.value[messages.value.length - 1].content)
        }

        messageReceived.value = true
      }
      catch (error) {
        console.error('Failed to send message:', error)
        messageReceived.value = true
      }
    }
  }
}

// Event handlers stored for cleanup
function handleKeypress(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey && messageReceived.value === true) {
    e.preventDefault()
    sendMessage()
  }
}

onMounted(() => {
  textareaEl.value!.addEventListener('keypress', handleKeypress)
})

onUnmounted(() => {
  textareaEl.value?.removeEventListener('keypress', handleKeypress)
})
</script>

<template>
  <div v-if="imageUrl" class="absolute w-auto h-[150px]  flex justify-start items-start pl-[10px] " :style="`bottom: ${extraInputHeight}px`">
    <div class="h-[80px] inline-flex">
      <CloseButton v-model="hovered" :on-click="clearImage" />
      <img ref="imageEl" :src="imageUrl" class="rounded-md border border-border h-[80px] inline-table">
    </div>
  </div>
  <div
    class="netwatch-textarea-wrapper textarea-enter"
  >
    <div class="netwatch-composer" :style="`height: ${56 + extraInputHeight}px;}`">
      <template v-if="!isListening">
        <pre ref="preEl">{{ textareaValue }}</pre>
        <textarea
          ref="textareaEl"
          v-model="textareaValue" name="message"
          placeholder="Write a reply..." aria-label="Write a reply..."
          tabindex="0" @input="countNewLines"
        />
      </template>
      <div v-else class="w-full h-full flex items-center gap-2 p-2">
        <div
          class="w-full h-full rounded-2xl bg-[#024895] p-2 flex items-center justify-between text-white text-xl"
        >
          <div class=" i-lucide-audio-lines  animate-[opacity-animation_1.5s_infinite] animate-alternate" />
          <div class="font-bold ">
            {{ timer }}
          </div>
          <button class="composer-button" :disabled="!messageReceived" aria-label="send recording" @click="sendAudio">
            <div class="i-pajamas-stop" />
          </button>
        </div>
        <button class="composer-button text-red text-xl" @click="deleteAudio">
          <div class="opacity-100 hover:opacity-70 i-bi-trash-fill " aria-label="delete recording" />
        </button>
      </div>
      <div v-if="!isListening" class="netwatch-composer-buttons">
        <!-- <Popover :open="emojiMenuOpen" @update:open="toggleEmojiMenu">
          <PopoverTrigger as-child>
            <button class="composer-button">
              <div :class="cn(' opacity-100 hover:opacity-70 text-lg', emojiMenuOpen ? 'i-fluent-emoji-28-filled text-[#0072A3] ' : 'i-fluent-emoji-28-regular text-[#757575]')" />
            </button>
          </PopoverTrigger>
          <PopoverContent class="w-80 h-[244px] m-0 px-0 bottom--2 right--10  z-2147483001 absolute" side="top">
            <div class="grid gap-4">
              <EmojiPicker :native="true" @select="onSelectEmoji" />
            </div>
          </PopoverContent>
        </Popover> -->
        <button v-if="canReply" class="composer-button text-[#0072A3] text-lg disabled:text-gray" :disabled="!messageReceived || streamingIsLoading" @click="sendMessage">
          <div v-if="streamingIsLoading" class="i-svg-spinners-90-ring-with-bg" />
          <div v-else class="i-ic-sharp-send" />
        </button>
        <template v-else>
          <button class="composer-button" @click="() => open()">
            <div class="opacity-100 hover:opacity-70 text-lg i-material-symbols-image-outline-rounded text-[#757575]" aria-label="attach image" />
          </button>
          <button class="composer-button text-[#757575] disabled:text-gray-200" :disabled="!speechEnabled" @click="startRecording">
            <div class=" opacity-100 hover:opacity-70 text-lg i-lucide-audio-lines" aria-label="record audio" />
          </button>
        </template>
      </div>
    </div>
  </div>
</template>
